import _ from 'lodash'

/**
 * Creates a rate limiter function for controlling request frequency.
 *
 * The returned function can be called before sending a network request to determine
 * whether it should be allowed or dropped based on the configured rate limit.
 *
 * @param {Object} options
 * @param {number} options.limit - Maximum number of allowed requests within the time window.
 * @param {number} options.windowMs - Duration of the time window in milliseconds.
 *
 * @returns {function(): boolean} A function that returns `true` if the request is allowed,
 *   or `false` if the rate limit has been exceeded. Also logs warnings when throttling
 *   starts or stops.
 */
const createRateLimiter = ({ limit, windowMs }: { limit: number; windowMs: number }): (() => boolean) => {
  let requestTimestamps = []
  let isThrottling = false

  return () => {
    const now = Date.now()

    // Remove timestamps that are outside the current window
    requestTimestamps = requestTimestamps.filter((timestamp) => now - timestamp < windowMs)

    const shouldAllow = requestTimestamps.length < limit

    // not throttling -> throttle
    if (!shouldAllow && !isThrottling) {
      isThrottling = true
      // console log is also captured by LogRocket, so no need to send a separate log to LogRocket
      console.warn(
        `[LogRocket] Network rate limit of ${limit}/${windowMs / 1000}s exceeded. ` +
          `Starting to drop network requests.`
      )
    }

    // throttled -> no throttling
    if (shouldAllow && isThrottling) {
      isThrottling = false
      console.warn(
        `[LogRocket] Network traffic is back below the limit. Resuming network recording.`
      )
    }

    if (shouldAllow) {
      // limit not reached, add the new timestamp and allow the request
      requestTimestamps.push(now)
      return true
    } else {
      // limit exceeded, don't record this request
      return false
    }
  }
}

// per AI overlord's recommendation
const MAX_REQUESTS_PER_WINDOW = 10 // allow X requests...
const RATE_LIMIT_WINDOW_MS = 5 * 1000 // ...within a Y-second window

const networkRateLimiter = createRateLimiter({
  limit: MAX_REQUESTS_PER_WINDOW,
  windowMs: RATE_LIMIT_WINDOW_MS,
})

export function responseSanitizer(response) {

  if (shouldRedact(response.url)) {
    return null
  }

  const maxBodySize = 10000
  const bodyLength = _.get(response, 'body.length', 0)
  if (bodyLength > maxBodySize) {
    try {
      console.log(`Truncating response payload of ${response.body?.length} for ${response.url}`)
      // A slow operation, but at least memory resident
      const body = JSON.parse(response.body)
      const newBody = []
      const children = Array.isArray(body) ? body : _.get(body, 'children', [])
      const key = Array.isArray(body) ? 'uniqueId' : 'data.uniqueId'
      for (const child of children) {
        newBody.push({
          uniqueId: _.get(child, key),
        })
      }
      return {
        ...response,
        body: JSON.stringify(newBody),
      }
    } catch (err) {
      console.log(`Failed to parse request body: ${err.message}`)
      return {
        ...response,
        body: null,
      }
    }
  }
  return response
}

export function requestSanitizer(request) {
  try {

    if (shouldRedact(request.url)) {
      return null
    }

    // Only rate-limit "read" requests like queries or fetches.
    // "Write" requests are important, so we don't rate-limit them.
    const isRead = isReadRequest(request)
    if (isRead && !networkRateLimiter()) {
      // Drop read requests that exceed the rate limit
      return null
    }

    if (!_.isNil(request.headers.Authorization)) {
      request.headers.Authorization = 'Bearer ********'
    }

    const maxBodySize = 20000
    const bodyLength = _.get(request, 'body.length', 0)
    if (bodyLength >= maxBodySize) {
      console.log(
        `Truncating request payload of ${request.body?.length} for ${request.method}:${request.url}`
      )
      // This might take a while
      const body = JSON.parse(request.body ?? '{}')
      return {
        ...request,
        body: JSON.stringify({
          uniqueId: _.get(body, 'uniqueId'),
          method: request.method,
        }),
      }
    }

    return request
  } catch (e) {
    console.log(`Failed to parse request body: ${e.message}`)
    // There may still be a password in there
    return {
      ...request,
      body: null,
    }
  }
}


function isReadRequest(request) {
  const method = request.method?.toUpperCase()
  const url = request.url?.toLowerCase() || ''

  switch (method) {
    case 'GET':
      return true
    case 'POST':
      return url.includes('/_mget') || url.includes('/query')
    case 'PUT':
    case 'PATCH':
    case 'DELETE':
      return false
    default:
      return true
  }
}

const REDACTED_ENDPOINTS = [
  'data:application/wasm',
  'uploadcare.com',
  'ucarecdn.com',
  'uploadcare.withvector',
  '/actions/system/sensitive-file/',
  'entities/actions/system/oauth2/token',
]

const shouldRedact = (url?: string) => {
  if (!url) return false
  const normalizedUrl = url.toLowerCase()
  return REDACTED_ENDPOINTS.some((pattern) => normalizedUrl.includes(pattern))
}
